import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from '../hooks/use-toast';
import { X, ArrowLeft } from 'lucide-react';

const PostJobPage = () => {
  const navigate = useNavigate();
  const [job, setJob] = useState({
    title: '',
    description: '',
    requirements: '',
    minPay: '',
    maxPay: '',
    location: 'Remote',
    type: 'Full-time',
    skills: []
  });
  const [newSkill, setNewSkill] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const addSkill = () => {
    if (newSkill && !job.skills.includes(newSkill)) {
      setJob({ ...job, skills: [...job.skills, newSkill] });
      setNewSkill('');
    }
  };

  const removeSkill = (skill) => {
    setJob({ ...job, skills: job.skills.filter(s => s !== skill) });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!job.title || !job.description || !job.minPay || !job.maxPay) {
      toast({
        title: 'Missing fields',
        description: 'Please fill in all required fields.',
        variant: 'destructive'
      });
      return;
    }

    setSubmitting(true);
    
    // Mock submission
    setTimeout(() => {
      toast({
        title: 'Job posted!',
        description: 'Your job has been successfully posted.'
      });
      setSubmitting(false);
      navigate('/company/jobs');
    }, 1000);
  };

  return (
    <div className="min-h-screen bg-[#F9FBF5] pt-20">
      <div className="max-w-4xl mx-auto px-6 py-12">
        <Button
          variant="ghost"
          className="mb-6"
          onClick={() => navigate('/company/jobs')}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to My Jobs
        </Button>

        <h1 className="text-3xl font-bold text-gray-900 mb-8">Post a New Job</h1>

        <Card className="p-8">
          <form onSubmit={handleSubmit}>
            <div className="space-y-6">
              {/* Job Title */}
              <div>
                <Label htmlFor="title">Job Title *</Label>
                <Input
                  id="title"
                  value={job.title}
                  onChange={(e) => setJob({ ...job, title: e.target.value })}
                  placeholder="e.g., Senior AI Engineer"
                  className="mt-2"
                />
              </div>

              {/* Description */}
              <div>
                <Label htmlFor="description">Job Description *</Label>
                <Textarea
                  id="description"
                  value={job.description}
                  onChange={(e) => setJob({ ...job, description: e.target.value })}
                  placeholder="Describe the role, responsibilities, and what makes this opportunity exciting..."
                  className="mt-2"
                  rows={6}
                />
              </div>

              {/* Requirements */}
              <div>
                <Label htmlFor="requirements">Requirements</Label>
                <Textarea
                  id="requirements"
                  value={job.requirements}
                  onChange={(e) => setJob({ ...job, requirements: e.target.value })}
                  placeholder="List the key requirements (one per line)\ne.g., 5+ years experience\nPython expertise\nML/AI knowledge"
                  className="mt-2"
                  rows={4}
                />
              </div>

              {/* Pay Range */}
              <div>
                <Label>Pay Range ($/hour) *</Label>
                <div className="grid grid-cols-2 gap-4 mt-2">
                  <Input
                    type="number"
                    placeholder="Min"
                    value={job.minPay}
                    onChange={(e) => setJob({ ...job, minPay: e.target.value })}
                  />
                  <Input
                    type="number"
                    placeholder="Max"
                    value={job.maxPay}
                    onChange={(e) => setJob({ ...job, maxPay: e.target.value })}
                  />
                </div>
              </div>

              {/* Location & Type */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label>Location *</Label>
                  <Select value={job.location} onValueChange={(value) => setJob({ ...job, location: value })}>
                    <SelectTrigger className="mt-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Remote">Remote</SelectItem>
                      <SelectItem value="Hybrid">Hybrid</SelectItem>
                      <SelectItem value="Onsite">Onsite</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Job Type *</Label>
                  <Select value={job.type} onValueChange={(value) => setJob({ ...job, type: value })}>
                    <SelectTrigger className="mt-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Full-time">Full-time</SelectItem>
                      <SelectItem value="Contract">Contract</SelectItem>
                      <SelectItem value="Part-time">Part-time</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Skills */}
              <div>
                <Label>Required Skills</Label>
                <div className="flex gap-2 mt-2 mb-3">
                  <Input
                    placeholder="Add a skill"
                    value={newSkill}
                    onChange={(e) => setNewSkill(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill())}
                  />
                  <Button type="button" onClick={addSkill}>Add</Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {job.skills.map((skill, idx) => (
                    <Badge key={idx} variant="secondary" className="text-sm px-3 py-1">
                      {skill}
                      <X
                        className="w-3 h-3 ml-2 cursor-pointer"
                        onClick={() => removeSkill(skill)}
                      />
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Submit */}
              <div className="flex gap-4 pt-6 border-t">
                <Button
                  type="submit"
                  className="bg-[#0A3900] hover:bg-[#1A5C0A]"
                  size="lg"
                  disabled={submitting}
                >
                  {submitting ? 'Posting...' : 'Post Job'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="lg"
                  onClick={() => navigate('/company/jobs')}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default PostJobPage;
